<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import Icon from '../components/Icon.svelte';
  import { tableIconName } from '../lib/icons';
  import { onDestroy } from 'svelte';
  import { subscribeSchemaSSE } from '../lib/schema-sse';
  import AppShell from '../components/AppShell.svelte';
  import FilterBuilder from '../components/FilterBuilder.svelte';
  import RecordEditor from '../components/RecordEditor.svelte';
  import {
    fetchPublicDataTable,
    fetchDataRecords,
    fetchDataRecord,
    createDataRecord,
    updateDataRecord,
    deleteDataRecord,
    bulkDeleteRecords,
    fetchAllRecordIds,
    reorderDataFields,
    type ApiTable,
    type ApiField,
    type ApiRecord,
    type ApiTableView,
  } from '../lib/data-tables-api';
  import {
    fetchKanbanFacets,
    fetchKanbanColumnRaw,
    fetchKanbanColumnIds,
    type KanbanSource,
  } from '../lib/kanban-api';
  import ViewPresetsMenu from '../components/ViewPresetsMenu.svelte';
  import ExportHistoryButton from '../components/ExportHistoryButton.svelte';
  import ViewModeToggle from '../components/ViewModeToggle.svelte';
  import ColumnVisibilityMenu from '../components/ColumnVisibilityMenu.svelte';
  import CellEdit from '../components/CellEdit.svelte';
  import KanbanBoard from '../components/KanbanBoard.svelte';
  import KanbanCardConfigModal from '../components/KanbanCardConfigModal.svelte';
  import KanbanBulkBar from '../components/KanbanBulkBar.svelte';
  import { groupableFields as kanbanGroupableFields, fieldSupportsDrag } from '../lib/kanban-grouping';
  import { loadCardConfig, saveCardConfig, loadViewMode, saveViewMode, loadColumnWidths, saveColumnWidths, type KanbanCardConfig, type KanbanGranularity } from '../lib/kanban-config';
  import { session } from '../lib/session';
  import { fmtInt } from '../lib/format';
  import { type FieldTypeKey } from '../lib/data-tables-catalog';
  import BulkEditModal from '../components/BulkEditModal.svelte';
  import { canViewField, type Role } from '../lib/roles';
  import { generateCsv, downloadCsv, columnsFromFields } from '../lib/csv-export';
  import { ApiError } from '../lib/api';
  import {
    emptyGroup,
    reassignIds,
    isEmpty,
    type Group,
    type FieldDef,
  } from '../lib/filter-tree';

  let { params }: { params?: { slug?: string } } = $props();
  const slug = $derived(params?.slug ?? '');

  let table = $state<ApiTable | null>(null);
  let records = $state<ApiRecord[]>([]);
  let total = $state(0);
  let page = $state(1);
  /** Sort: null = default (createdAt desc); 'custom:fieldKey:asc|desc' = JSONB sort. */
  let sort = $state<string | null>(null);

  /** View mode: list (таблиця) або kanban (дошка). */
  let viewMode = $state<'list' | 'kanban'>('list');
  let kanbanFieldKey = $state<string | null>(null);

  /** Гранулярність дат для kanban (date/datetime групування). */
  let granularity = $state<KanbanGranularity>('month');
  /** Лічильник перевантаження дошки (фільтри/поле/granularity/realtime). */
  let kanbanReload = $state(0);

  /** Налаштування картки (поля + розмір) — з localStorage. */
  let cardConfig = $state<KanbanCardConfig>({ primaryFieldKey: null, fields: [], cardSize: 'medium' });
  let cardConfigOpen = $state(false);

  /** Поля доступні для налаштування картки. */
  const cardConfigFields = $derived(
    (table?.fields ?? []).filter((f) => !f.hidden && f.type !== 'linked_summary'),
  );

  /** Поля по яких можна групувати у kanban (усі типи крім обчислюваних). */
  const groupableFields = $derived(
    kanbanGroupableFields((table?.fields ?? []).filter((f) => f.show_in_list !== false)),
  );
  const kanbanField = $derived(groupableFields.find((f) => f.fieldKey === kanbanFieldKey) ?? null);
  const kanbanFieldType = $derived(kanbanField?.type ?? 'single_line');

  /** Чи можна переміщувати картки drag-and-drop (admin, не built-in, settable). */
  const canDragKanban = $derived(
    ($session?.role === 'ADMIN') && !!table && !table.builtIn && !!kanbanField && fieldSupportsDrag(kanbanField),
  );

  const primaryField = $derived(
    (table?.fields ?? []).find((f) => f.primary) ?? visibleColumns[0] ?? null,
  );

  /** Серверне джерело даних дошки (facets/cards/ids). */
  function kanbanParams(): URLSearchParams {
    const p = new URLSearchParams();
    p.set('groupField', kanbanFieldKey ?? '');
    if (kanbanFieldType === 'date' || kanbanFieldType === 'datetime') p.set('granularity', granularity);
    return p;
  }
  const kanbanSource: KanbanSource = {
    loadFacets: (cp) => fetchKanbanFacets(`/data-tables/${slug}/records/kanban`, kanbanParams(), cp),
    loadCards: async (k, pg) => {
      const r = await fetchKanbanColumnRaw<ApiRecord>(`/data-tables/${slug}/records/kanban`, kanbanParams(), k, pg);
      return { records: r.items, total: r.total };
    },
    loadIds: async (k) => (await fetchKanbanColumnIds(`/data-tables/${slug}/records/kanban`, kanbanParams(), k)).ids,
  };

  function bumpKanban() { kanbanReload += 1; }

  function onSwitchMode(m: 'list' | 'kanban') {
    viewMode = m;
    if (m === 'kanban') {
      cardConfig = loadCardConfig(slug) ?? cardConfig;
      bumpKanban();
    }
  }

  function saveCard(c: KanbanCardConfig) {
    cardConfig = c;
    saveCardConfig(slug, c);
    cardConfigOpen = false;
  }

  /** Інлайн-редагування поля на картці kanban (board оновлює локально). */
  async function onKanbanCellEdit(recordId: string, field: ApiField, value: unknown) {
    const rec = await fetchDataRecord(slug, recordId).catch(() => null);
    const base = rec ? { ...rec.values } : {};
    if (value === null || value === '') delete base[field.fieldKey];
    else base[field.fieldKey] = value;
    await updateDataRecord(slug, recordId, base);
  }

  /** Переміщення картки між колонками — встановлює значення group-поля. */
  async function onKanbanCardMove(recordId: string, newValue: unknown) {
    if (!kanbanFieldKey) return;
    const rec = await fetchDataRecord(slug, recordId).catch(() => null);
    const base = rec ? { ...rec.values } : {};
    if (newValue === null || newValue === '') delete base[kanbanFieldKey];
    else base[kanbanFieldKey] = newValue;
    await updateDataRecord(slug, recordId, base);
  }

  /** Realtime: debounced bump дошки при record-changed з SSE. */
  let kanbanRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleKanbanRefresh() {
    if (viewMode !== 'kanban') return;
    if (kanbanRefreshTimer) clearTimeout(kanbanRefreshTimer);
    kanbanRefreshTimer = setTimeout(() => { kanbanRefreshTimer = null; bumpKanban(); }, 600);
  }

  function toggleSortCustom(fieldKey: string) {
    if (suppressSortClick) return;
    const desc = `custom:${fieldKey}:desc`;
    const asc = `custom:${fieldKey}:asc`;
    sort = sort === desc ? asc : sort === asc ? desc : desc;
    page = 1;
    void load();
  }
  function sortIconCustom(fieldKey: string): string {
    if (sort === `custom:${fieldKey}:desc`) return '▼';
    if (sort === `custom:${fieldKey}:asc`) return '▲';
    return '↕';
  }

  const SORTABLE_FIELD_TYPES = new Set([
    'number', 'datetime', 'date', 'currency', 'auto_number', 'percent',
    'checkbox', 'formula', 'linked_summary', 'stat',
  ]);
  function isSortableType(type: string): boolean {
    return SORTABLE_FIELD_TYPES.has(type);
  }

  /** Drag&drop reorder колонок (HTML5 native). Передає новий порядок positions
   *  на бекенд через reorderDataFields → reload schema. */
  let draggedColId = $state<string | null>(null);
  let dropTargetColId = $state<string | null>(null);

  // ── Resize колонок (table-fixed, ширина зберігається у localStorage, ключ — slug) ──
  const COL_MIN = 80, COL_MAX = 600;
  let colWidths = $state<Record<string, number>>({});
  $effect(() => { colWidths = loadColumnWidths(slug, '__list__'); });
  function defColW(col: ApiField): number {
    if (colWidths[col.fieldKey]) return colWidths[col.fieldKey];
    const t = col.type;
    if (t === 'email') return 220;
    if (t === 'multi_line') return 240;
    if (t === 'datetime' || t === 'date') return 150;
    if (t === 'number' || t === 'currency' || t === 'percent' || t === 'auto_number' || t === 'stat') return 120;
    if (t === 'checkbox') return 90;
    return 170;
  }
  function colStyle(key: string): string {
    const col = visibleColumns.find((c) => c.fieldKey === key);
    return `width:${col ? defColW(col) : 170}px`;
  }
  let colResizing: { key: string; startX: number; startW: number } | null = null;
  /** Блокує sort-клік на заголовку одразу після ресайзу. */
  let suppressSortClick = false;
  function startColResize(e: PointerEvent, key: string) {
    e.preventDefault();
    e.stopPropagation();
    suppressSortClick = true;
    const th = (e.currentTarget as HTMLElement).parentElement as HTMLElement;
    const startW = colWidths[key] ?? th.getBoundingClientRect().width;
    colResizing = { key, startX: e.clientX, startW };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onColResizeMove(e: PointerEvent) {
    if (!colResizing) return;
    const w = Math.max(COL_MIN, Math.min(COL_MAX, colResizing.startW + (e.clientX - colResizing.startX)));
    colWidths = { ...colWidths, [colResizing.key]: w };
  }
  function endColResize() {
    if (!colResizing) return;
    saveColumnWidths(slug, '__list__', colWidths);
    colResizing = null;
    setTimeout(() => { suppressSortClick = false; }, 250);
  }

  function onColDragStart(e: DragEvent, fieldId: string) {
    if (!e.dataTransfer) return;
    draggedColId = fieldId;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldId);
  }
  function onColDragOver(e: DragEvent, fieldId: string) {
    if (!draggedColId || draggedColId === fieldId) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    dropTargetColId = fieldId;
  }
  function onColDragLeave() {
    dropTargetColId = null;
  }
  async function onColDrop(e: DragEvent, targetFieldId: string) {
    e.preventDefault();
    const sourceId = draggedColId;
    draggedColId = null;
    dropTargetColId = null;
    if (!sourceId || sourceId === targetFieldId) return;

    const cols = [...visibleColumns];
    const fromIdx = cols.findIndex((c) => c.id === sourceId);
    const toIdx = cols.findIndex((c) => c.id === targetFieldId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = cols.splice(fromIdx, 1);
    cols.splice(toIdx, 0, moved!);
    // Передаємо position 0..N + groupName кожного поля (no-op якщо groupName був той самий).
    try {
      await reorderDataFields(
        slug,
        cols.map((c, i) => ({ id: c.id, position: i, groupName: c.groupName ?? null })),
      );
      // Reload schema щоб table.fields оновився.
      const refreshed = await fetchPublicDataTable(slug);
      table = refreshed;
    } catch (e2) {
      err = e2 instanceof Error ? e2.message : String(e2);
    }
  }
  const pageSize = 50;

  let loading = $state(true);
  let err = $state<string | null>(null);
  let busy = $state(false);

  // Toolbar.
  let search = $state('');
  let appliedTree = $state<Group>(emptyGroup());
  let draftTree = $state<Group>(emptyGroup());
  let showBuilder = $state(false);

  // Record editor + actions dropdown.
  let actionsMenuOpen = $state(false);
  let editorOpen = $state(false);
  let editingRecord = $state<ApiRecord | null>(null);

  // Multi-select + bulk-delete state (як в Persons).
  let selected = $state<Set<string>>(new Set());
  let confirmOpen = $state(false);
  let confirmBusy = $state(false);
  let confirmMsg = $state<string | null>(null);
  const isAdmin = $derived($session?.role === 'ADMIN');
  const allOnPageSelected = $derived(
    records.length > 0 && records.every((r) => selected.has(r.id)),
  );

  function toggleRow(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selected = next;
  }
  function togglePage() {
    const next = new Set(selected);
    if (allOnPageSelected) {
      for (const r of records) next.delete(r.id);
    } else {
      for (const r of records) next.add(r.id);
    }
    selected = next;
  }
  function clearSelection() {
    selected = new Set();
  }
  async function selectAllUnderFilter() {
    if (!slug) return;
    try {
      const r = await fetchAllRecordIds(slug);
      selected = new Set(r.ids);
    } catch (e) {
      showAlert((e as Error).message);
    }
  }
  async function doBulkDelete() {
    if (!slug || selected.size === 0) return;
    confirmBusy = true;
    try {
      const ids = Array.from(selected);
      const r = await bulkDeleteRecords(slug, ids);
      confirmOpen = false;
      confirmMsg = `Видалено ${r.deleted} записів`;
      setTimeout(() => (confirmMsg = null), 4000);
      clearSelection();
      await loadRecords();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      confirmBusy = false;
    }
  }

  async function loadTable() {
    if (!slug) return;
    try {
      table = await fetchPublicDataTable(slug);
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    }
  }

  async function loadRecords() {
    if (!slug || !table || table.builtIn) {
      records = [];
      total = 0;
      return;
    }
    try {
      const r = await fetchDataRecords(slug, { page, pageSize, sort: sort ?? undefined });
      records = r.items;
      total = r.total;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    }
  }

  async function load() {
    loading = true;
    err = null;
    await loadTable();
    await loadRecords();
    loading = false;
  }
  let unsubSchemaSSE: (() => void) | null = null;

  onDestroy(() => unsubSchemaSSE?.());

  // Перемикання між таблицями (slug) — відновлюємо збережений вид + завантажуємо.
  $effect(() => {
    if (slug) {
      const vm = loadViewMode(slug);
      viewMode = vm?.viewMode ?? 'list';
      kanbanFieldKey = vm?.kanbanFieldKey ?? null;
      granularity = vm?.granularity ?? 'month';
      if (viewMode === 'kanban') { cardConfig = loadCardConfig(slug) ?? cardConfig; bumpKanban(); }
      void load();
      unsubSchemaSSE?.();
      unsubSchemaSSE = subscribeSchemaSSE(
        slug,
        () => void loadTable(),
        () => scheduleKanbanRefresh(),
      );
    }
  });

  // Зберігаємо вид при зміні (per-table).
  $effect(() => {
    if (slug) saveViewMode(slug, { viewMode, kanbanFieldKey, granularity });
  });

  /** ApiField → FieldDef. Тип передаємо as-is: catalog FieldTypeKey =
   *  filter-tree FieldType (єдина база). */
  function fieldsForFilter(fields: ApiField[]): FieldDef[] {
    return fields.map((f) => ({ key: f.fieldKey, label: f.label, type: f.type }));
  }

  const currentRole = $derived(($session?.role ?? null) as Role | null);

  /** Колонки видимі у таблиці: не-hidden, show_in_list (default true), не linked-types.
   *  Phase 4: filter за min_view_role. */
  /** In-memory override від view-пресету (null = використовує DB show_in_list). */
  let columnOverride = $state<string[] | null>(null);

  const visibleColumns = $derived<ApiField[]>(
    columnOverride
      ? (columnOverride
          .map((key) => (table?.fields ?? []).find((f) => f.fieldKey === key))
          .filter(Boolean)
          .filter((f) => canViewField(currentRole, f!.min_view_role)) as ApiField[])
      : (table?.fields ?? []).filter(
          (f) =>
            !f.hidden &&
            f.show_in_list !== false &&
            canViewField(currentRole, f.min_view_role),
        ),
  );

  /** Сумарна ширина колонок (table-fixed): зміна однієї колонки лише розширює
   *  таблицю (горизонтальний скрол), не зменшуючи інші. */
  const totalColW = $derived(
    (($session?.role === 'ADMIN') ? 44 : 0) + visibleColumns.reduce((s, c) => s + defColW(c), 0),
  );

  function applyViewPreset(v: ApiTableView) {
    if (v.columns.length > 0) columnOverride = v.columns;
    if (v.sortKey) sort = v.sortKey;
    if (v.filters) appliedTree = v.filters as Group;
    page = 1;
    void load();
  }

  /** Bulk-edit modal — admin вибирає одне editable поле + значення, застосовує до
   *  всіх selected. У custom-таблицях усі поля — editable (немає built-in). */
  let bulkEditOpen = $state(false);
  const bulkEditableFields = $derived<ApiField[]>(
    (table?.fields ?? []).filter(
      (f) =>
        !f.hidden &&
        !f.is_computed &&
        f.type !== 'linked_record' &&
        f.type !== 'linked_summary',
    ),
  );
  async function applyBulkEditToOne(id: string, field: ApiField, value: unknown): Promise<void> {
    // PATCH замінює увесь values → потрібні поточні значення. Беремо з list-records
    // або тягнемо з сервера (kanban-вибір може містити записи з інших сторінок).
    const cached = records.find((r) => r.id === id);
    const current = cached ?? (await fetchDataRecord(slug, id).catch(() => null));
    const nextValues = { ...(current?.values ?? {}) };
    if (value === null) delete nextValues[field.fieldKey];
    else nextValues[field.fieldKey] = value;
    await updateDataRecord(slug, id, nextValues);
  }
  const exportParams = $derived<Record<string, unknown>>({
    columns: visibleColumns.map((f) => f.fieldKey),
    sort: sort ?? undefined,
    filter: !activeFilterEmpty ? JSON.stringify(appliedTree) : undefined,
  });

  async function onBulkEditDone(progress: { done: number; total: number; errors: number }) {
    if (progress.errors > 0) {
      err = `Помилки під час масового редагування: ${progress.errors} з ${progress.total}`;
    }
    bulkEditOpen = false;
    clearSelection();
    await load();
  }

  /** Чи можна редагувати клітинку поля (admin, custom-таблиця, не обчислюване). */
  function isCellEditable(f: ApiField): boolean {
    if (!isAdmin || !table || table.builtIn) return false;
    if (f.is_computed || f.type === 'formula' || f.type === 'stat' || f.type === 'linked_summary') {
      return false;
    }
    return true;
  }

  /** Зберегти одне поле запису (popover-редактор клітинки). Кидає помилку — CellEdit покаже alert. */
  async function persistCell(recordId: string, field: ApiField, value: unknown) {
    const idx = records.findIndex((r) => r.id === recordId);
    if (idx < 0) return;
    const current = records[idx]!;
    const v = value === '' || value === undefined ? null : value;
    const nextValues = { ...current.values };
    if (v === null) delete nextValues[field.fieldKey];
    else nextValues[field.fieldKey] = v;
    await updateDataRecord(slug, recordId, nextValues);
    records[idx] = { ...current, values: nextValues };
  }

  function openFilter() {
    draftTree = JSON.parse(JSON.stringify(appliedTree));
    reassignIds(draftTree);
    showBuilder = true;
  }
  function applyFilter() {
    appliedTree = JSON.parse(JSON.stringify(draftTree));
    showBuilder = false;
  }
  function clearFilter() {
    appliedTree = emptyGroup();
    draftTree = emptyGroup();
    showBuilder = false;
  }
  function cancelFilter() {
    showBuilder = false;
  }
  const activeFilterEmpty = $derived(isEmpty(appliedTree));

  function openCreate() {
    editingRecord = null;
    editorOpen = true;
  }
  function closeEditor() {
    editorOpen = false;
    editingRecord = null;
  }

  async function onSaveRecord(values: Record<string, unknown>) {
    if (!slug) return;
    busy = true;
    try {
      if (editingRecord) {
        await updateDataRecord(slug, editingRecord.id, values);
      } else {
        await createDataRecord(slug, values);
      }
      closeEditor();
      await loadRecords();
    } catch (e) {
      showAlert(formatSaveError(e));
    } finally {
      busy = false;
    }
  }

  /** Дружнє повідомлення для duplicate-помилки 409. */
  function formatSaveError(e: unknown): string {
    if (e instanceof ApiError && e.code === 'data-table.duplicate') {
      const details = (e.details as { fieldKeys?: string[] } | undefined) ?? {};
      const fks = details.fieldKeys ?? [];
      const labels = fks
        .map((fk) => table?.fields.find((f) => f.fieldKey === fk)?.label ?? fk)
        .join(' + ');
      return `${e.message}\n\nКонфлікт по полях: ${labels}`;
    }
    return e instanceof Error ? e.message : String(e);
  }

  async function onDeleteRecord() {
    if (!slug || !editingRecord) return;
    if (!confirm('Видалити цей запис?')) return;
    busy = true;
    try {
      await deleteDataRecord(slug, editingRecord.id);
      closeEditor();
      await loadRecords();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  /** Формат значення комірки для display. */
  function fmtCell(v: unknown, t: FieldTypeKey): string {
    if (v === undefined || v === null || v === '') return '—';
    if (t === 'checkbox') return v ? '✓' : '—';
    if (t === 'currency' && typeof v === 'number') return v.toLocaleString('uk-UA') + ' ₴';
    if (t === 'date' && typeof v === 'string') {
      // ISO YYYY-MM-DD або повний ISO datetime.
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toLocaleDateString('uk-UA');
    }
    return String(v);
  }

  /** Локальна фільтрація search'ем + filter tree (поки що тільки search). */
  const visibleRecords = $derived.by(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) =>
      Object.values(r.values).some((v) => String(v ?? '').toLowerCase().includes(q)),
    );
  });

  const pageCount = $derived(Math.max(1, Math.ceil(total / pageSize)));
</script>

<svelte:window onpointermove={onColResizeMove} onpointerup={endColResize} />

<AppShell active={`table:${slug}`}>
  {#if loading}
    <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
  {:else if err}
    <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
  {:else if table}
    <div class="mx-auto max-w-[1200px]">
      <!-- Header -->
      <header class="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div class="flex items-center gap-3">
          <div
            class="grid h-11 w-11 shrink-0 place-items-center rounded-full text-white"
            style="background: {table.color}"
          >
            <Icon name={tableIconName(table.icon)} size={20} />
          </div>
          <div>
            <h1 class="text-xl font-semibold leading-tight">
              {table.name}
              {#if !table.builtIn}
                <span class="text-sm text-[var(--color-text-muted)] font-medium ml-1">· {fmtInt(total)}</span>
              {/if}
            </h1>
            <p class="mt-0.5 text-[12px] text-[var(--color-text-muted)] min-h-[16px]">{table.description ?? ''}</p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <a
            href={`#/settings/data-structure/tables/${slug}`}
            class="rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)] inline-flex items-center gap-1.5"
          >
            <Icon name="settings" size={14} /> Налаштування
          </a>
          {#if !table.builtIn}
            <div class="relative inline-flex">
              <button
                onclick={() => { actionsMenuOpen = false; openCreate(); }}
                class="rounded-l-md btn-pu px-3 py-1.5 text-xs font-semibold"
              >
                + Новий запис
              </button>
              <button
                onclick={() => (actionsMenuOpen = !actionsMenuOpen)}
                aria-label="Більше дій"
                class="inline-flex items-center rounded-r-md btn-pu border-l border-white/30 px-2 py-1.5 text-xs"
              >
                <Icon name="chevronDown" size={15} />
              </button>
              {#if actionsMenuOpen}
                <button
                  type="button"
                  onclick={() => (actionsMenuOpen = false)}
                  class="fixed inset-0 z-20 cursor-default"
                  aria-label="Close menu"
                ></button>
                <div class="absolute right-0 top-full mt-1 z-30 min-w-[200px] rounded-md border border-[var(--color-border-3)] bg-white shadow-lg overflow-hidden">
                  <button
                    type="button"
                    onclick={() => { actionsMenuOpen = false; openCreate(); }}
                    class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-[var(--color-bg-3)]"
                  >
                    <Icon name="add" size={14} /><span>Новий запис</span>
                  </button>
                  <a
                    href={`#/settings/data-structure/import/${slug}`}
                    onclick={() => (actionsMenuOpen = false)}
                    class="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[var(--color-bg-3)] no-underline text-[var(--color-text)]"
                  >
                    <Icon name="download" size={14} /><span>Імпорт з файлу</span>
                  </a>
                </div>
              {/if}
            </div>
          {/if}
        </div>
      </header>

      <!-- Toolbar: два рядки -->
      <div class="mb-4 space-y-2">
        <!-- Рядок 1: Пошук + Фільтр + CSV -->
        <div class="relative flex flex-wrap items-center gap-2">
          <div class="w-full sm:flex-1 relative">
            <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted-2)] flex"><Icon name="search" size={14} /></span>
            <input
              bind:value={search}
              placeholder="Пошук у {table.name}"
              class="w-full rounded-md border border-[var(--color-border-3)] bg-white pl-8 pr-2.5 py-1.5 text-xs"
            />
          </div>
          <button
            onclick={openFilter}
            class="relative inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors
              {!activeFilterEmpty
                ? 'border-[var(--color-pu)] bg-purple-50 text-[var(--color-pu-3)]'
                : 'border-[var(--color-border-3)] bg-white text-[var(--color-text)] hover:bg-[var(--color-bg-3)]'}"
          >
            <Icon name="filter" size={14} /> Фільтр
            {#if !activeFilterEmpty}
              <span class="ml-1 inline-block rounded-full bg-[var(--color-pu)] text-white text-[9.5px] font-bold px-1.5 py-0.5 leading-none">
                ●
              </span>
            {/if}
          </button>
          {#if !activeFilterEmpty}
            <button onclick={clearFilter} class="text-[10.5px] text-[var(--color-text-muted)] hover:underline">
              скинути всі
            </button>
          {/if}
          {#if slug}
            <ExportHistoryButton
              {slug}
              label="{table?.name ?? slug} · {new Date().toLocaleDateString('uk-UA')}"
              totalCount={total}
              params={exportParams}
            />
          {/if}
        </div>

        <!-- Рядок 2: Вид + Видимість + Views -->
        <div class="flex flex-wrap items-center gap-2">
          <ViewModeToggle
            mode={viewMode}
            {kanbanFieldKey}
            {groupableFields}
            {granularity}
            onModeChange={onSwitchMode}
            onKanbanFieldChange={(k) => { kanbanFieldKey = k; if (viewMode === 'kanban') bumpKanban(); }}
            onGranularityChange={(g) => { granularity = g; if (viewMode === 'kanban') bumpKanban(); }}
          />
          {#if slug && table && viewMode === 'list'}
            <ColumnVisibilityMenu {slug} fields={table.fields} onChanged={loadTable} />
          {/if}
          {#if slug}
            <ViewPresetsMenu
              {slug}
              currentColumns={visibleColumns.map((f) => f.fieldKey)}
              currentSortKey={sort}
              currentFilters={!activeFilterEmpty ? appliedTree : null}
              onApply={applyViewPreset}
              {isAdmin}
            />
          {/if}
        </div>
      </div>

      <!-- Bulk action bar (list-view; для kanban — плаваюча KanbanBulkBar) -->
      {#if viewMode === 'list' && isAdmin && !table?.builtIn && selected.size > 0}
        <div class="mb-2 flex items-center gap-3 rounded-lg border border-[var(--color-pu)]/40 bg-purple-50 px-3.5 py-2 text-xs">
          <span class="font-semibold">Вибрано: {selected.size}</span>
          {#if selected.size < total}
            <button onclick={selectAllUnderFilter} class="text-[var(--color-pu-3)] hover:underline">
              Вибрати все ({fmtInt(total)})
            </button>
          {/if}
          <button onclick={clearSelection} class="text-[var(--color-text-muted)] hover:underline ml-auto">
            скинути
          </button>
          {#if bulkEditableFields.length > 0}
            <button
              onclick={() => (bulkEditOpen = true)}
              class="inline-flex items-center gap-1.5 rounded-md btn-pu px-3 py-1.5 text-[11px] font-semibold"
            ><Icon name="edit" size={13} /> Редагувати</button>
          {/if}
          <button
            onclick={() => (confirmOpen = true)}
            class="inline-flex items-center gap-1.5 rounded-md bg-red-600 text-white px-3 py-1.5 text-[11px] font-semibold hover:bg-red-700"
          >
            <Icon name="delete" size={13} /> Видалити
          </button>
        </div>
      {/if}
      {#if confirmMsg && !confirmOpen}
        <div class="mb-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] text-emerald-700">
          {confirmMsg}
        </div>
      {/if}

      <!-- Kanban board (замість таблиці) -->
      {#if viewMode === 'kanban' && kanbanFieldKey}
        <KanbanBoard
          {slug}
          groupFieldKey={kanbanFieldKey}
          groupFieldType={kanbanFieldType}
          source={kanbanSource}
          reloadToken={kanbanReload}
          canDragGroup={canDragKanban}
          fields={cardConfigFields}
          config={cardConfig}
          onConfigClick={() => (cardConfigOpen = true)}
          onRecordClick={(id) => push(`/tables/${slug}/${id}`)}
          onCellEdit={isAdmin && !table.builtIn ? onKanbanCellEdit : undefined}
          onCardMove={canDragKanban ? onKanbanCardMove : undefined}
          selectable={isAdmin && !table.builtIn}
          selectedIds={selected}
          onSelectionChange={(s) => (selected = s)}
        />
        <KanbanBulkBar
          count={selected.size}
          onEdit={bulkEditableFields.length > 0 ? () => (bulkEditOpen = true) : undefined}
          onDelete={() => (confirmOpen = true)}
          onClear={clearSelection}
        />
      {:else}

      <!-- Table -->
      <div class="overflow-x-auto rounded-2xl border border-[var(--color-border-2)] bg-[var(--color-surface)] shadow-card">
        <table class="table-fixed text-[13px] [&_td]:overflow-hidden [&_th]:overflow-hidden" style="width: {totalColW}px;">
          <thead class="sticky top-0 z-[1] glass">
            <tr class="text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-text-muted-2)] border-b border-[var(--color-border-2)]">
              {#if isAdmin && !table?.builtIn}
                <th class="text-left px-3 py-2 w-[30px]">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onclick={(e) => { e.stopPropagation(); togglePage(); }}
                    title="Вибрати усі на цій сторінці"
                  />
                </th>
              {/if}
              {#each visibleColumns as f (f.id)}
                {@const isDropTarget = dropTargetColId === f.id}
                {#if !isSortableType(f.type)}
                  <!-- Текстові/контактні/enum типи не сортуються — лише drag. -->
                  <th
                    style={colStyle(f.fieldKey)} class="relative group/col text-left px-3 py-2 font-bold whitespace-nowrap select-none {isDropTarget ? 'bg-[var(--color-pu)]/10 outline-2 outline-dashed outline-[var(--color-pu)]' : ''}"
                    ondragover={(e) => onColDragOver(e, f.id)}
                    ondragleave={onColDragLeave}
                    ondrop={(e) => onColDrop(e, f.id)}
                    title="drag щоб переміщати колонку"
                  >
                    {#if isAdmin && !table.builtIn}<span draggable="true" ondragstart={(e) => onColDragStart(e, f.id)} onclick={(e) => e.stopPropagation()} class="absolute left-1.5 top-1/2 -translate-y-1/2 inline-flex text-[var(--color-text-muted-2)] cursor-grab opacity-0 group-hover/col:opacity-100 transition-opacity z-[1]" title="Перетягнути колонку"><Icon name="grip" size={13} /></span>{/if}
<span class="block truncate align-middle transition-transform duration-150 group-hover/col:translate-x-[15px]">{f.label}</span>
                    <div class="col-resize" role="separator" aria-orientation="vertical" onpointerdown={(e) => startColResize(e, f.fieldKey)}></div>
                  </th>
                {:else}
                  <th
                    style={colStyle(f.fieldKey)} class="relative group/col text-left px-3 py-2 font-bold whitespace-nowrap cursor-pointer select-none hover:bg-[var(--color-bg-2)] {isDropTarget ? 'bg-[var(--color-pu)]/10 outline-2 outline-dashed outline-[var(--color-pu)]' : ''}"
                    ondragover={(e) => onColDragOver(e, f.id)}
                    ondragleave={onColDragLeave}
                    ondrop={(e) => onColDrop(e, f.id)}
                    onclick={() => toggleSortCustom(f.fieldKey)}
                    title="Сортувати · drag щоб переміщати колонку"
                  >
                    {#if isAdmin && !table.builtIn}<span draggable="true" ondragstart={(e) => onColDragStart(e, f.id)} onclick={(e) => e.stopPropagation()} class="absolute left-1.5 top-1/2 -translate-y-1/2 inline-flex text-[var(--color-text-muted-2)] cursor-grab opacity-0 group-hover/col:opacity-100 transition-opacity z-[1]" title="Перетягнути колонку"><Icon name="grip" size={13} /></span>{/if}
<span class="block truncate align-middle transition-transform duration-150 group-hover/col:translate-x-[15px]">{f.label}
                    <span class="text-[var(--color-pu-3)]">{sortIconCustom(f.fieldKey)}</span></span>
                    <div class="col-resize" role="separator" aria-orientation="vertical" onpointerdown={(e) => startColResize(e, f.fieldKey)}></div>
                  </th>
                {/if}
              {/each}
            </tr>
          </thead>
          <tbody>
            {#if table.builtIn}
              <tr>
                <td colspan={visibleColumns.length} class="px-3 py-12 text-center text-sm text-[var(--color-text-muted)]">
                  Built-in таблиця — записи показуються на сторінці
                  {#if table.slug === 'users'}<a class="underline" href="#/persons">Користувачі</a>{:else}<a class="underline" href="#/payments">Платежі</a>{/if}.
                </td>
              </tr>
            {:else if visibleRecords.length === 0}
              <tr>
                <td colspan={visibleColumns.length + (isAdmin ? 1 : 0)} class="px-3 py-12 text-center text-sm text-[var(--color-text-muted)]">
                  {#if search.trim()}
                    Нічого не знайдено за запитом "{search}".
                  {:else if total === 0}
                    <div class="mb-2 inline-flex text-[var(--color-text-muted-2)]"><Icon name="inbox" size={30} /></div>
                    <div class="font-semibold mb-1">Поки немає записів</div>
                    <div class="text-[12px] text-[var(--color-text-muted-2)]">
                      Натисніть <button class="underline" onclick={openCreate}>"+ Новий запис"</button> щоб створити перший.
                    </div>
                  {:else}
                    Жодного запису не відповідає фільтру.
                  {/if}
                </td>
              </tr>
            {:else}
              {#each visibleRecords as r (r.id)}
                <tr
                  class="border-b border-[var(--color-border-2)] cursor-pointer hover:bg-[var(--color-pu)]/[0.04] transition-colors duration-150"
                  class:selected-row={selected.has(r.id)}
                  onclick={() => (window.location.hash = `#/tables/${slug}/${r.id}`)}
                >
                  {#if isAdmin}
                    <td class="px-3 py-2 align-top" onclick={(e) => { e.stopPropagation(); toggleRow(r.id); }}>
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onclick={(e) => { e.stopPropagation(); toggleRow(r.id); }}
                      />
                    </td>
                  {/if}
                  {#each visibleColumns as f (f.id)}
                    <td class="px-3 py-2 align-top text-[12.5px]">
                      <CellEdit
                        field={f}
                        value={r.values[f.fieldKey]}
                        record={r.values}
                        editable={isCellEditable(f)}
                        onSave={(v) => persistCell(r.id, f, v)}
                      />
                    </td>
                  {/each}
                </tr>
              {/each}
            {/if}
          </tbody>
        </table>
      </div>
      {/if}

      <!-- Pagination -->
      {#if !table.builtIn && total > pageSize}
        <div class="mt-3 flex items-center justify-between text-[11px] text-[var(--color-text-muted)]">
          <span>Всього: {total}</span>
          <div class="flex items-center gap-1">
            <button
              onclick={() => { page = Math.max(1, page - 1); loadRecords(); }}
              disabled={page <= 1}
              class="rounded border border-[var(--color-border-3)] bg-white px-2 py-0.5 disabled:opacity-50"
            >‹</button>
            <span class="px-2">{page} / {pageCount}</span>
            <button
              onclick={() => { page = Math.min(pageCount, page + 1); loadRecords(); }}
              disabled={page >= pageCount}
              class="rounded border border-[var(--color-border-3)] bg-white px-2 py-0.5 disabled:opacity-50"
            >›</button>
          </div>
        </div>
      {/if}
    </div>
  {/if}

  {#if confirmOpen}
    <div class="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onclick={() => (confirmOpen = false)}>
      <div onclick={(e) => e.stopPropagation()} class="w-[min(440px,90vw)] rounded-xl bg-white shadow-2xl p-5">
        <h3 class="text-base font-bold mb-2">Видалити вибрані?</h3>
        <p class="text-xs text-[var(--color-text-muted)] mb-4">
          Будуть видалені <strong>{selected.size}</strong> записів. Цю дію не можна скасувати.
        </p>
        <div class="flex justify-end gap-2">
          <button
            onclick={() => (confirmOpen = false)}
            disabled={confirmBusy}
            class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-xs font-medium"
          >Скасувати</button>
          <button
            onclick={doBulkDelete}
            disabled={confirmBusy}
            class="rounded-md bg-red-600 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          >{confirmBusy ? 'Видаляю…' : 'Видалити назавжди'}</button>
        </div>
      </div>
    </div>
  {/if}
</AppShell>

<FilterBuilder
  open={showBuilder}
  bind:root={draftTree}
  customFields={table ? fieldsForFilter(table.fields) : []}
  onApply={applyFilter}
  onCancel={cancelFilter}
  onClear={clearFilter}
/>

<RecordEditor
  open={editorOpen}
  fields={table?.fields ?? []}
  record={editingRecord}
  busy={busy}
  onSave={onSaveRecord}
  onCancel={closeEditor}
  onDelete={editingRecord ? onDeleteRecord : undefined}
/>

<BulkEditModal
  open={bulkEditOpen}
  selectedIds={Array.from(selected)}
  editableFields={bulkEditableFields}
  entityLabel={selected.size === 1 ? 'запису' : 'записів'}
  onApply={applyBulkEditToOne}
  onClose={() => (bulkEditOpen = false)}
  onDone={onBulkEditDone}
/>

{#if cardConfigOpen}
  <KanbanCardConfigModal
    {slug}
    availableFields={cardConfigFields}
    config={cardConfig}
    sampleValues={records[0]?.values ?? {}}
    onSave={saveCard}
    onClose={() => (cardConfigOpen = false)}
  />
{/if}

<style>
  tr.selected-row { background: var(--color-pu-light); box-shadow: inset 2px 0 0 var(--color-pu); }
</style>
